import type { ErrorOption, UseFormSetError } from 'react-hook-form'
import type { AddressValidationResponse } from '../lib/client/types'
import type { DotFormKeys } from '../types'
import type { FormValues } from '../form/schema'
import submitValidateAddress from '../lib/client/submitValidateAddress'

const GENERIC_ERROR = `We couldn't validate this address. Please double-check the info and try again.`
const SERVICE_ERROR = `We're sorry, something went wrong. Please try again later.`

const validateAddress = async (
  { streetAddress, city, state, unit = '', zip }: FormValues,
  reactHookFormSetError: UseFormSetError<FormValues>,
  pCode?: string
): Promise<boolean> => {
  // Assume form is valid
  let valid = true

  // Anytime setError is invoked, form is no longer valid
  function setError(name: DotFormKeys<FormValues>, error: ErrorOption) {
    reactHookFormSetError(name, error)
    valid = false
  }

  let data: AddressValidationResponse
  try {
    data = await submitValidateAddress({
      street1: streetAddress,
      street2: unit,
      city,
      state,
      zip,
      ...(pCode ? { inviteCode: pCode } : {}),
    })
    valid = data.valid
    if (valid) {
      return true
    }
  } catch {
    setError('streetAddress', {
      type: 'custom',
      message: SERVICE_ERROR,
    })
    setError('generalAddress', {
      type: 'custom',
      message: SERVICE_ERROR,
    })
    return false
  }

  const { address, errors } = data

  if (errors.length) {
    const { errorType } = errors[0]

    if (['INVALID_PO_BOX_IN_ADDRESS', 'INVALID_RECORD_TYPE_POST_OFFICE'].includes(errorType)) {
      setError('streetAddress', {
        type: 'custom',
        message: 'Address cannot be a PO Box',
      })
    } else if (['INVALID_ADDR_ZIP_DIFF'].includes(errorType)) {
      // Use address.zip as a suggestion
      setError('suggestions.zip', {
        type: 'custom',
        message: address?.zip,
      })
    } else if (
      ['INVALID_ADDR_SPELLING', 'INVALID_ADDR_CITY_NAME', 'INVALID_ADDR_NOT_IN_ZIP5'].includes(
        errorType
      )
    ) {
      const isCity = city !== address?.city

      setError(`suggestions.${isCity ? 'city' : 'state'}`, {
        type: 'custom',
        message: isCity ? address?.city : address?.state,
      })
    } else if (
      [
        'INVALID_ADDR_CARDINAL_DIR',
        'INVALID_ADDR_CHANGED_ADDRESS_COMPONENT',
        'INVALID_ADDR_STREET_SPELLING',
      ].includes(errorType)
    ) {
      setError('suggestions.streetAddress', {
        type: 'custom',
        message: address?.street1,
      })
    } else if (['INVALID_ADDR_MISSING_SECONDARY_NUMBER'].includes(errorType)) {
      setError('unit', {
        type: 'custom',
        message: 'Enter unit / apartment #',
      })
    } else {
      setError('streetAddress', {
        type: 'custom',
        message: GENERIC_ERROR,
      })
      setError('generalAddress', {
        type: 'custom',
        message: GENERIC_ERROR,
      })
    }
  }

  return valid
}
export default validateAddress
